"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.materializeTable = void 0;
var metricValue_1 = require("../features/metricValue");
var window_1 = require("../features/window");
var models_1 = require("../models");
var EffectiveDefinition_1 = require("../models/EffectiveDefinition");
var Result_1 = require("../models/Result");
var DefaultTransformRegistry_1 = require("./DefaultTransformRegistry");
/**
 * The top level data transformation function.
 *
 * Responsible for converting any calculated columns into concrete values,
 * preparing the table for visualization and interactivity.
 *
 * ### Interface
 * The interface is designed to be robust to significant changes in
 * implementation approach.
 *
 * If we decide to re-write this logic in a different language to support
 * execution on the server, or re-implement for performance reasons, this
 * interface should (hopefully) not have to change in any significant way.
 *
 * ### Implementation
 * At a high level, the current implementation is designed to fascilitate easily
 * adding new calculated column types, and is not particularly optimized for
 * performance.
 *
 * The definition is inspected, and converted into a series of {@link
 * TransformFunction}s. First, the `window` feature is applied, then calculated
 * columns.
 *
 * #### SIGNIFICANT LIMITATIONS:
 * The implementation does not currently support arbitrary composition of column
 * types.
 *
 * The two `table` transforms, {@link RowShift} and {@link TimeShif}
 * read from the `sourceTable` rather than the `previousStep`, which means they
 * can't reference any calculated columns.
 *
 * In future changes, we should remove these limitiations.
 *
 * @param definition The definition to materialize
 * @param sourceTable The source data to start with
 * @param registry The `TransformRegistry` to use for calculation lookup.
 * Defaults to `DefaultTransformRegistry`.
 * @returns The fully materialized table, or an error explaining why
 * materialization was unsuccessful
 */
var materializeTable = function (definition, sourceTable, registry) {
    if (registry === void 0) { registry = DefaultTransformRegistry_1.DefaultTransformRegistry; }
    try {
        var transforms = generateTransforms(definition, registry);
        return (0, Result_1.Ok)(applyTransforms(transforms, sourceTable));
    }
    catch (e) {
        return (0, Result_1.Err)(new Result_1.MetricsEngineError((e === null || e === void 0 ? void 0 : e.message) || 'Failed to materialize table'));
    }
};
exports.materializeTable = materializeTable;
var generateTransforms = function (definition, registry) {
    var windowTransforms = generateWindowTransforms(definition);
    var columnTransforms = generateColumnTransforms(definition, registry);
    return windowTransforms.concat(columnTransforms);
};
var generateColumnTransforms = function (definition, registry) {
    return Object.entries(definition.schema)
        .filter(function (_a) {
        var key = _a[0], columnSchema = _a[1];
        return columnSchema.calculation !== undefined && !(0, EffectiveDefinition_1.isTableCalcRef)(columnSchema.calculation);
    })
        .map(function (_a) {
        var key = _a[0], columnSchema = _a[1];
        var calcType = columnSchema.calculation.type;
        var creator = registry[calcType];
        if (creator === undefined) {
            throw new Error("Couldn't find transform creator in registry: ".concat(calcType, ", ").concat(Object.keys(registry)));
        }
        return creator(definition, key);
    });
};
var generateWindowTransforms = function (definition) {
    var window = (0, window_1.getFirstWindowOrDefault)(definition);
    var accessor = (0, metricValue_1.buildTimestampAccessor)(definition);
    return [function (sourceTable, previousStep) { return (0, window_1.applyWindow)(previousStep, window, (0, models_1.throwIfUndefined)(accessor)); }];
};
var applyTransforms = function (transforms, sourceTable) {
    return transforms.reduce(function (previousStep, transform) { return transform(sourceTable, previousStep); }, sourceTable);
};
//# sourceMappingURL=materializeTable.js.map